import React from "react";
import { ArrowRight, Trash2, Pencil, Sparkles } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import HoverVideo from "@/components/HoverVideo";

const Tile = ({ t, testid }) => {
  if (!t) return <div className="bg-white/[0.03]" />;
  const src = mediaUrl(t.url);
  const video = t.type.startsWith("video") || /\.(mp4|webm|mov)(\?|$)/i.test(t.url);
  return video
    ? <HoverVideo src={src} autoInView className="w-full h-full object-cover" testid={testid} />
    : <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" data-testid={testid} />;
};

const Collage = ({ p }) => (
  <div className="relative aspect-[16/10] overflow-hidden rounded-t-2xl bg-[#0B1013]" data-testid={`project-collage-${p.id}`}>
    {p.thumbs?.length ? (
      <div className="grid grid-cols-2 grid-rows-2 gap-0.5 w-full h-full transition-transform duration-500 group-hover:scale-[1.03]">
        {[0, 1, 2, 3].map((i) => <Tile key={i} t={p.thumbs[i]} testid={`project-thumb-${p.id}-${i}`} />)}
      </div>
    ) : (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-zinc-400" style={{ background: "radial-gradient(circle at 70% 20%, rgba(95,192,200,0.18), transparent 60%)" }}>
        <Sparkles className="w-6 h-6 text-[#5FC0C8]/70" /><span className="text-[11px] font-mono uppercase tracking-widest">No creations yet</span>
      </div>
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1013] via-transparent to-transparent pointer-events-none" />
  </div>
);

export default function ProjectCard({ p, onOpen, onRename, onDelete }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div data-testid={`project-card-${p.id}`} onClick={onOpen}
      className="ax-card ax-glass-card cursor-pointer group relative overflow-hidden !p-0">
      <Collage p={p} />
      <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={stop(onRename)} data-testid={`project-rename-${p.id}`} title="Rename"
          className="h-8 w-8 rounded-lg bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={stop(onDelete)} data-testid={`project-delete-${p.id}`} title="Delete"
          className="h-8 w-8 rounded-lg bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-300 hover:text-red-400">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-5 pt-3">
        <h3 className="font-heading text-xl font-semibold line-clamp-1" data-testid={`project-name-${p.id}`}>{p.name}</h3>
        <p className="text-sm text-zinc-500 mt-1 line-clamp-1">{p.description || "No description"}</p>
        <div className="mt-4 flex items-center justify-between text-xs font-mono text-zinc-600 uppercase tracking-widest">
          <span>{new Date(p.created_at).toLocaleDateString()}</span>
          <span className="flex items-center gap-1 text-[#5FC0C8] group-hover:translate-x-1 transition-transform">Open <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </div>
  );
}
